import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import type { QuickSearchSelectionMetadata } from "@/types/QuickSearch";

const clearAction = createAction("clear");

export type ManagerLmsAccessStatus =
  | "idle"
  | "loading"
  | "ready"
  | "error";

export interface ManagerLmsAccessState {
  status: ManagerLmsAccessStatus;
  subjectId: string | null;
  selection: QuickSearchSelectionMetadata | null;
  hasCourseEnrolments: boolean | null;
}

export const initialManagerLmsAccessState: ManagerLmsAccessState = {
  status: "idle",
  subjectId: null,
  selection: null,
  hasCourseEnrolments: null,
};

const managerLmsAccessSlice = createSlice({
  name: "managerLmsAccess",
  initialState: initialManagerLmsAccessState,
  reducers: {
    setManagerLmsAccessLoading: (state, action: PayloadAction<string>) => {
      if (
        state.status === "ready" &&
        action.payload !== "" &&
        state.subjectId === action.payload &&
        state.selection !== null
      ) {
        return; // Retain mounted LMS screens while the same company's access is revalidated.
      }
      state.status = "loading";
      state.subjectId = action.payload;
      state.selection = null;
      state.hasCourseEnrolments = null;
    },
    setManagerLmsAccessReady: (
      state,
      action: PayloadAction<{
        subjectId: string;
        selection: QuickSearchSelectionMetadata | null;
        hasCourseEnrolments?: boolean | null;
      }>,
    ) => {
      state.status = "ready";
      state.subjectId = action.payload.subjectId;
      state.selection = action.payload.selection;
      state.hasCourseEnrolments = action.payload.hasCourseEnrolments ?? null;
    },
    setManagerLmsAccessError: (state, action: PayloadAction<string>) => {
      state.status = "error";
      state.subjectId = action.payload;
      state.selection = null;
      state.hasCourseEnrolments = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearAction, () => initialManagerLmsAccessState);
  },
});

export const {
  setManagerLmsAccessLoading,
  setManagerLmsAccessReady,
  setManagerLmsAccessError,
} = managerLmsAccessSlice.actions;
export default managerLmsAccessSlice.reducer;
